import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Zap,
  Clock,
  MessageSquare,
  Target,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const FeaturesSection = () => {
  const navigate = useNavigate();
  const features = [
    {
      icon: Shield,
      title: "Enterprise Security",
      description:
        "Bank-level encryption and security protocols to protect your data and messages",
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description:
        "Messages delivered in milliseconds with our optimized infrastructure",
      color: "from-yellow-500 to-orange-500",
      bgColor: "from-yellow-50 to-orange-100",
    },
    {
      icon: Clock,
      title: "24/7 Reliability",
      description: "Round-the-clock service with 99.9% uptime guarantee",
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
    },
  ];

  const capabilities = [
    {
      icon: MessageSquare,
      title: "Bulk SMS",
      description: "Send bulk messages to your customers with ease",
    },
    {
      icon: Target,
      title: "Smart Targeting",
      description: "Advanced audience segmentation and optimized delivery",
    },
    {
      icon: CheckCircle,
      title: "Delivery Reliability",
      description: "Reliable delivery for every message",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hover: {
      scale: 1.1,
      rotate: 360,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
  };

  return (
    <section className="py-12 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 sm:py-16 md:py-20">
      <div className="container px-2 mx-auto sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-12 text-center sm:mb-16"
        >
          <h2 className="px-2 mb-4 text-2xl font-bold text-white sm:mb-6 sm:text-3xl md:text-4xl lg:text-5xl">
            Powerful Features for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-blue-500">
              Modern Businesses
            </span>
          </h2>
          <p className="px-4 mx-auto max-w-3xl text-base text-gray-300 sm:text-lg md:text-xl">
            Everything you need to build, scale, and optimize your messaging
            campaigns
          </p>
        </motion.div>

        {/* Main Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          className="grid grid-cols-1 gap-3 mb-16 sm:gap-4 sm:mb-20 md:gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="relative group"
            >
              <div
                className={`p-6 sm:p-8 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.bgColor} border border-gray-200 shadow-xl hover:shadow-2xl transition-all duration-300 h-full mx-1 sm:mx-0`}
              >
                <motion.div
                  variants={iconVariants}
                  whileHover="hover"
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:shadow-lg transition-all duration-300`}
                >
                  <feature.icon className="w-6 h-6 text-white sm:w-8 sm:h-8" />
                </motion.div>

                <h3 className="mb-3 text-xl font-bold text-gray-900 sm:mb-4 sm:text-2xl">
                  {feature.title}
                </h3>

                <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                  {feature.description}
                </p>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-br to-transparent rounded-xl opacity-0 transition-opacity duration-300 sm:rounded-2xl from-white/10 group-hover:opacity-100" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Capabilities Section */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="p-6 rounded-2xl backdrop-blur-sm sm:p-8 md:p-12 sm:rounded-3xl bg-white/10"
        >
          <div className="mb-8 text-center sm:mb-12">
            <h3 className="mb-3 text-2xl font-bold text-white sm:mb-4 sm:text-3xl md:text-4xl">
              Advanced Capabilities
            </h3>
            <p className="px-4 text-base text-gray-300 sm:text-lg md:text-xl">
              Professional tools and integrations for enterprise-level messaging
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ scale: 1.05 }}
                className="flex items-start p-4 mx-1 space-x-3 rounded-lg border backdrop-blur-sm transition-all duration-300 sm:p-6 sm:space-x-4 sm:rounded-xl bg-white/5 border-white/10 hover:bg-white/10 sm:mx-0"
              >
                <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-red-500 to-blue-500 rounded-lg sm:w-12 sm:h-12 sm:rounded-xl">
                  <capability.icon className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="mb-1 text-base font-semibold text-white sm:mb-2 sm:text-lg">
                    {capability.title}
                  </h4>
                  <p className="text-xs text-gray-300 sm:text-sm">
                    {capability.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 text-center sm:mt-16"
        >
          <motion.button
            onClick={() => navigate("/login")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 w-full text-base font-semibold text-white bg-gradient-to-r from-red-500 to-blue-500 rounded-full shadow-lg transition-all duration-300 sm:w-auto sm:px-12 sm:py-4 sm:text-lg hover:shadow-xl"
          >
            Get Started
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
